// Models
// Utilities
import { getDiff } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import { calculateOrderHintFromExpression } from "@ms/uno-utilities/lib/local/OrderHintUtilities";
import { generateOrderHintBetween } from "@ms/uno-utilities/lib/local/OrdinalUtilities";
import isEmpty from "lodash/isEmpty";
import { isConditionalColoringColumnId, isConditionalColoringOperator } from "./ConditionalColoringUtilities";
/** Converts a service rule resource to the client rule model. */ export function deserializeConditionalColoringRule(resource, entityName) {
    return {
        id: resource.id,
        version: resource["@etag"],
        orderHint: resource.orderHint,
        ...deserializeConditionalColoringExpression(resource.expression, entityName)
    };
}
/** Deserializes one rule without allowing malformed optional rule data to fail its parent Plan. */ export function tryDeserializeConditionalColoringRule(resource, entityName) {
    try {
        return deserializeConditionalColoringRule(resource, entityName);
    } catch  {
        return undefined;
    }
}
/** Serializes ordered client rules into create resources. */ export function serializeConditionalColoringRulesForCreate(rules) {
    // Rule order is client-owned; sort explicitly because service collections do not guarantee ordering.
    const ordered = [
        ...rules
    ].sort((left, right)=>left.order - right.order);
    let previousHint = null;
    return ordered.map((rule)=>{
        const orderHint = generateChainedOrderHint(previousHint, null);
        // Resolve locally (mirroring the service) so the next rule brackets after this one.
        previousHint = calculateOrderHintFromExpression(orderHint);
        return serializeConditionalColoringRuleForCreate(rule, orderHint);
    });
}
/** Converts a client rule to the full resource shape used by Plan persistence. */ export function serializeConditionalColoringRuleForPersistence(rule) {
    return {
        id: rule.id,
        "@etag": rule.version ?? "",
        expression: serializeConditionalColoringRuleExpression(rule),
        orderHint: rule.orderHint ?? ""
    };
}
/** Builds create, update, and delete delta entries for changed rules. */ export function serializeConditionalColoringRulesForUpdate(currentRules, sourceRules) {
    const result = [];
    const currentIds = new Set(currentRules.map((rule)=>rule.id));
    for (const sourceRule of sourceRules){
        if (!currentIds.has(sourceRule.id) && sourceRule.version) {
            result.push({
                "@removed": {},
                "@id": sourceRule.id,
                "@etag": sourceRule.version
            });
        }
    }
    // Rule order is client-owned; sort explicitly because service collections do not guarantee ordering.
    const ordered = [
        ...currentRules
    ].sort((left, right)=>left.order - right.order);
    let previousHint = null;
    ordered.forEach((currentRule, index)=>{
        const sourceRule = sourceRules.find((rule)=>rule.id === currentRule.id);
        // New rule: bracket after the previous rule and before the next resolved neighbour, chaining a
        // run of new rules so their resolved hints preserve order.
        if (!sourceRule?.version) {
            const orderHint = generateChainedOrderHint(previousHint, findNextConditionalColoringOrderHint(ordered, index));
            previousHint = calculateOrderHintFromExpression(orderHint);
            result.push(serializeConditionalColoringRuleForCreate(currentRule, orderHint));
            return;
        }
        const moved = currentRule.order !== sourceRule.order;
        if (isEmpty(getDiff(sourceRule, currentRule, undefined, true))) {
            // Unchanged existing rule still anchors subsequent new rules.
            previousHint = currentRule.orderHint ?? previousHint;
            return;
        }
        const update = {
            "@id": currentRule.id,
            "@etag": currentRule.version ?? sourceRule.version,
            expression: serializeConditionalColoringRuleExpression(currentRule)
        };
        // Only re-position the rule (send an order hint) when its evaluation order actually changed.
        if (moved) {
            const orderHint = generateChainedOrderHint(previousHint, findNextConditionalColoringOrderHint(ordered, index));
            update.orderHint = orderHint;
            previousHint = calculateOrderHintFromExpression(orderHint);
        } else {
            previousHint = currentRule.orderHint ?? previousHint;
        }
        result.push(update);
    });
    return result.length > 0 ? result : undefined;
}
/** Applies rule deltas, returning updated rules or `undefined` for a no-op. */ export function applyConditionalColoringRulesDelta(currentRules, deltaRules, entityName) {
    if (deltaRules.length === 0) {
        return undefined;
    }
    const rulesById = new Map(currentRules.map((rule)=>[
            rule.id,
            rule
        ]));
    let hasChanges = false;
    for (const deltaRule of deltaRules){
        if ("@removed" in deltaRule) {
            hasChanges = rulesById.delete(deltaRule["@id"]) || hasChanges;
            continue;
        }
        const existingRule = rulesById.get(deltaRule.id);
        if (existingRule) {
            rulesById.set(deltaRule.id, {
                ...existingRule,
                ...deltaRule.expression === undefined ? {} : deserializeConditionalColoringExpression(deltaRule.expression, entityName),
                version: deltaRule["@etag"] ?? existingRule.version,
                orderHint: deltaRule.orderHint ?? existingRule.orderHint
            });
        } else {
            if (deltaRule.expression === undefined) {
                throw createConditionalColoringDeserializationError(entityName, "Expression");
            }
            if (deltaRule.orderHint === undefined) {
                throw createConditionalColoringDeserializationError(entityName, "OrderHint");
            }
            rulesById.set(deltaRule.id, deserializeConditionalColoringRule({
                ...deltaRule,
                expression: deltaRule.expression,
                orderHint: deltaRule.orderHint
            }, entityName));
        }
        hasChanges = true;
    }
    // Rule order is client-owned; sort explicitly because service collections do not guarantee ordering.
    return hasChanges ? Array.from(rulesById.values()).sort((left, right)=>left.order - right.order) : undefined;
}
/** Parses and validates a client-owned rule expression blob. */ function deserializeConditionalColoringExpression(expression, entityName) {
    try {
        const parsedExpression = JSON.parse(expression);
        if (!isConditionalColoringExpression(parsedExpression)) {
            throw new Error("Invalid conditional coloring expression.");
        }
        return parsedExpression;
    } catch  {
        throw createConditionalColoringDeserializationError(entityName, "Expression");
    }
}
function createConditionalColoringDeserializationError(entityName, key) {
    return new Error(`Deserialization Error: [Entity=${entityName}][Field=ConditionalColoringRules][Key=${key}]`);
}
function isConditionalColoringExpression(value) {
    if (!isRecord(value)) {
        return false;
    }
    const expression = value.expression;
    if (!isRecord(expression)) {
        return false;
    }
    const firstValue = expression.firstValue;
    const secondValue = expression.secondValue;
    const hasValidFirstValue = typeof firstValue === "string" || typeof firstValue === "number" || Array.isArray(firstValue) && firstValue.every((item)=>typeof item === "string");
    const hasValidSecondValueIfPresent = secondValue === undefined || typeof secondValue === "string" || typeof secondValue === "number";
    return isConditionalColoringColumnId(value.columnId) && typeof value.colorIndex === "number" && typeof value.order === "number" && isConditionalColoringOperator(expression.operator) && hasValidFirstValue && hasValidSecondValueIfPresent;
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function serializeConditionalColoringRuleExpression(rule) {
    return JSON.stringify({
        columnId: rule.columnId,
        colorIndex: rule.colorIndex,
        expression: rule.expression,
        order: rule.order
    });
}
function generateChainedOrderHint(previousHint, nextHint) {
    if (previousHint && nextHint && previousHint >= nextHint) {
        return generateOrderHintBetween(previousHint, null);
    }
    return generateOrderHintBetween(previousHint, nextHint);
}
function findNextConditionalColoringOrderHint(orderedRules, index) {
    for(let next = index + 1; next < orderedRules.length; next++){
        const hint = orderedRules[next].orderHint;
        if (hint) {
            return hint;
        }
    }
    return null;
}
function serializeConditionalColoringRuleForCreate(rule, orderHint) {
    return {
        expression: serializeConditionalColoringRuleExpression(rule),
        orderHint
    };
}
